import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import SEO from '../components/SEO';
import JsonLd, { breadcrumbSchema } from '../components/JsonLd';
import ResourceCard from '../components/ResourceCard';
import { resources as mockResources } from '../data/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResourcesIndexPage() {
  const [resources, setResources] = useState(mockResources);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const controller = new AbortController();
    fetch(`${API}/posts`, { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => { if (data?.length > 0) setResources(data); })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(resources.map((r) => r.category).filter(Boolean));
    return ['All', ...Array.from(set)];
  }, [resources]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resources.filter((r) => {
      if (activeCategory !== 'All' && r.category !== activeCategory) return false;
      if (!q) return true;
      return (
        (r.title || '').toLowerCase().includes(q) ||
        (r.excerpt || '').toLowerCase().includes(q) ||
        (r.category || '').toLowerCase().includes(q)
      );
    });
  }, [resources, query, activeCategory]);

  return (
    <div className="pt-0 lg:pt-28 pb-20 bg-white min-h-screen">
      <SEO
        title="Resources & News"
        description="Case studies, product updates and practical guidance on Print MIS, workflow automation and integration from the Imprint technical team."
        url="https://imprint-mis.co.uk/resources"
      />
      <JsonLd data={breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Resources', path: '/resources' },
      ])} />

      {/* Hero */}
      <section className="bg-gray-50 border-b border-gray-100 py-12 lg:py-16">
        <div className="section-container">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#00BCE4] transition-colors mb-6">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <p
            className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            From the Technical Team
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="resources-index-heading"
          >
            Resources <span className="text-[#00BCE4]">&amp; News</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl" style={{ fontFamily: 'Inter, sans-serif' }}>
            Case studies, product updates and practical guidance on Print MIS,
            workflow automation and integration.
          </p>
        </div>
      </section>

      {/* Toolbar + Grid */}
      <section className="py-12 lg:py-16">
        <div className="section-container">
          <div className="mb-10 flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="search"
                data-testid="resources-index-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search resources..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-[#00BCE4] focus:ring-2 focus:ring-[#00BCE4]/20 transition-all duration-300 outline-none"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="resources-index-filters">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  data-testid={`resources-index-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-300 ${
                    activeCategory === cat
                      ? 'bg-[#00BCE4] text-white shadow-md shadow-[#00BCE4]/30'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div data-testid="resources-index-empty" className="text-center py-16 text-gray-400">
              <p className="text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                No resources match your search. Try a different keyword or category.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="resources-index-grid">
              {filtered.map((resource) => (
                <ResourceCard key={resource.slug} resource={resource} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
